/**
 * A hub page opened with a session cookie whose session has gone (expired or
 * signed out elsewhere). Middleware only checks the cookie is there, so the
 * page rendered with no account and every screen said it "could not be
 * loaded". The layout now tells the shell the cookie is dead, and the shell
 * sends the athlete through /api/participant/auth/session-ended, which checks
 * the cookie again, clears it if it really is dead, and hands over to sign in
 * with a return URL to where they were.
 *
 * The check is repeated on the server because the shell's account can be
 * stale: a layout read on /hub/login (dead cookie) is kept across the client
 * navigation that follows signing in, by which time the cookie is new and
 * good. Then the trip just comes back to the page with a fresh layout.
 */

import { safeRedirect } from '@/lib/myfitvent-auth/form';

export const SESSION_ENDED_PATH = '/api/participant/auth/session-ended';
/** sessionStorage mark: one trip per tab per minute (stale-layout-refresh.ts). */
export const SESSION_ENDED_KEY = 'fv-hub-session-ended';

/** Hub pages that need a session (the same list middleware guards). */
export function isProtectedHubPath(pathname: string): boolean {
  if (pathname !== '/hub' && !pathname.startsWith('/hub/')) return false;
  if (pathname === '/hub/login') return false;
  if (pathname.startsWith('/hub/u/')) return false;
  return true;
}

/**
 * Where to come back to: a hub page on this site, never the login page
 * itself (that would land a signed-in athlete back on sign in). Anything else
 * becomes the hub home.
 */
export function hubReturnTarget(param: string | null | undefined): string {
  const target = safeRedirect(param, '/hub/home');
  const path = target.split(/[?#]/)[0];
  if (!isProtectedHubPath(path)) return '/hub/home';
  return target;
}

/** The trip the shell takes from a page whose session has gone. */
export function sessionEndedHref(pathname: string, search = ''): string {
  return `${SESSION_ENDED_PATH}?redirect=${encodeURIComponent(hubReturnTarget(pathname + search))}`;
}

/** Sign in, then back to `target` (the same shape middleware sends). */
export function hubLoginHref(target: string): string {
  return `/hub/login?redirect=${encodeURIComponent(hubReturnTarget(target))}`;
}
